import { useEffect, useRef } from 'react'

import {
  GATEWAY_URLS,
  HEALTH_TIMINGS,
  browserWentOnline,
  connectionCheckScheduled,
  selectConnectionOnline,
  type ClientRuntimeExtra,
  type ConnectionState,
} from '@krp/client-runtime'

import { useAppSelector } from './hooks'
import type { AppDispatch, RootState } from './types'

/**
 * Связь с сервером: проверки доступности и реакция экранов на её возврат (решение владельца 23.10.2026). Состояние —
 * срез `connection` (`@krp/client-runtime`), итог каждого запроса сообщает транспорт (`store.ts`), паузы ведёт
 * `ConnectionMonitor`, окно — `ConnectionOverlay`.
 */

export const selectConnection = (state: RootState): ConnectionState => state.connection
const selectOnline = (state: RootState): boolean => selectConnectionOnline(state.connection)

/** Пауза перед проверкой с этим номером: `HEALTH_TIMINGS.serverCheckPausesMs` — 1, 2, 4, 8, затем каждые 10 с. */
export const checkPauseOf = (attempt: number): number => {
  const pauses = HEALTH_TIMINGS.serverCheckPausesMs
  return pauses[Math.min(Math.max(0, attempt), pauses.length - 1)] ?? 10_000
}

type AppThunk<R = void> = (dispatch: AppDispatch, getState: () => RootState, extra: ClientRuntimeExtra) => R

/**
 * Назначить следующую проверку с паузой по номеру попытки — пока связи нет: и когда сервер не отвечает, и когда браузер говорит «нет
 * сети» (событие `online` приходит не всегда, RP-SALE-FAST, К-111).
 */
export const scheduleNextCheck = (): AppThunk => (dispatch, getState) => {
  const { status, attempts } = getState().connection
  if (status === 'online') return
  dispatch(connectionCheckScheduled({ at: Date.now() + checkPauseOf(attempts), attempt: attempts + 1 }))
}

/**
 * Одна проверка доступности: «кто я» Gateway (`/auth/me`) — тот же адрес, которым оболочка перепроверяет вход
 * (`useSessionRecovery`). Любой HTTP-ответ сервера, включая 401, значит «связь есть»: восстановление отмечает сам транспорт
 * (`onConnection` → `connectionRestored`), 401 уходит в общий механизм входа. Без повторов транспорта: паузы ведёт монитор.
 */
export const checkServer = (): AppThunk<Promise<void>> => async (dispatch, getState, { http }) => {
  if (getState().connection.status === 'online') return
  await http.request({ url: GATEWAY_URLS.me, noRetry: true, timeoutMs: HEALTH_TIMINGS.serverCheckTimeoutMs })
  dispatch(scheduleNextCheck())
}

/**
 * «Повторить сейчас» окна связи: проверка немедленно, без ожидания паузы. Браузер считал себя без сети — всё равно
 * проверяем: человек нажал, значит, видит сеть (Wi-Fi вернулся раньше события `online`).
 */
export const connectionCheckRequested = (): AppThunk => (dispatch, getState) => {
  dispatch(browserWentOnline())
  const { status, attempts } = getState().connection
  if (status !== 'reconnecting') return
  dispatch(connectionCheckScheduled({ at: Date.now(), attempt: Math.max(1, attempts) }))
}

/**
 * Перечитать подписку, когда связь с сервером вернулась (переход статуса в `online`). Замена собственных таймеров
 * восстановления экранов: пока связи нет, окно связи по центру, а экран не повторяет запросы сам. `enabled: false` —
 * подписка пропущена (`skipToken`): RTK бросает на `refetch` незапущенного запроса.
 */
export function useRefetchOnReconnect(refetch: () => unknown, enabled = true): void {
  const online = useAppSelector(selectOnline)
  const latest = useRef({ refetch, enabled })
  useEffect(() => {
    latest.current = { refetch, enabled }
  })
  const wasOnline = useRef(online)
  useEffect(() => {
    const was = wasOnline.current
    wasOnline.current = online
    if (!online || was || !latest.current.enabled) return
    try {
      latest.current.refetch()
    } catch {
      // Подписка снята между рендерами: перечитывать нечего.
    }
  }, [online])
}
